import type { ReactNode } from 'react'
import { useEffect, useRef, useCallback, useId } from 'react'

import { tv } from 'tailwind-variants'

import { Text } from '@/components/atoms/Text/Text'
import { cn } from '@/utils'

export interface IDialogProps {
  isOpen: boolean
  onClose: () => void
  title: string
  description?: ReactNode | string
  children?: ReactNode
  orientation?: 'row' | 'column'
  type: 'success' | 'error' | 'warning' | 'info'
  customIcon?: string
  hideCloseButton?: boolean
  iconBackground?: string
  iconFill?: string
  iconSize?: 'sm' | 'md' | 'lg'
  titleSize?: 'title-lg' | 'title-xl'
}

const iconSizeConfig = {
  sm: { text: '!text-icon-sm', wrapper: 'size-6' },
  md: { text: '!text-icon-md', wrapper: 'size-8' },
  lg: { text: '!text-icon-lg', wrapper: 'size-10' }
}

export const dialogVariants = tv({
  slots: {
    iconWrapperSlot:
      'mb-4 flex items-center justify-center rounded-full border',
    iconSlot: 'material-symbols-rounded'
  },
  variants: {
    type: {
      success: {
        iconWrapperSlot: 'bg-success-bg border-success-stroke',
        iconSlot: 'text-success-icon'
      },
      error: {
        iconWrapperSlot: 'bg-error-bg border-error-stroke',
        iconSlot: 'text-error-icon'
      },
      warning: {
        iconWrapperSlot: 'bg-warning-bg border-warning-stroke',
        iconSlot: 'text-warning-icon'
      },
      info: {
        iconWrapperSlot: 'bg-info-bg border-info-stroke',
        iconSlot: 'text-info-icon'
      }
    }
  }
})

/**
 * @deprecated Use `Dialog`/`DialogContent`/`DialogStatusIcon`/`DialogTitle`/`DialogDescription`/`DialogActions` from `@alternative/acqua/organisms` instead. acqua's DialogStatusIcon covers the same success/warning/error icon pattern; API is not 1:1.
 */
export const Dialog = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  customIcon,
  orientation = 'column',
  type = 'success',
  hideCloseButton = false,
  iconBackground,
  iconFill,
  iconSize = 'md',
  titleSize = 'title-lg'
}: IDialogProps): JSX.Element | null => {
  const dialogRef = useRef<HTMLDivElement>(null)
  const previousActiveElement = useRef<HTMLElement | null>(null)
  const titleId = useId()
  const descriptionId = useId()

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return

      if (event.key === 'Escape') {
        onClose()
        return
      }

      if (event.key === 'Tab') {
        if (!dialogRef.current) return

        const focusableElements =
          dialogRef.current.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault()
            lastElement?.focus()
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault()
            firstElement?.focus()
          }
        }
      }
    },
    [isOpen, onClose]
  )

  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement
      document.addEventListener('keydown', handleKeyDown)

      setTimeout(() => {
        if (dialogRef.current) {
          const focusableElements =
            dialogRef.current.querySelectorAll<HTMLElement>(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
          if (focusableElements.length > 0) {
            focusableElements[0].focus()
          } else {
            dialogRef.current.focus()
          }
        }
      }, 0)
    } else {
      if (previousActiveElement.current) {
        previousActiveElement.current.focus()
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, handleKeyDown])

  if (!isOpen) return null

  const { iconWrapperSlot, iconSlot } = dialogVariants({
    type: iconBackground || iconFill ? undefined : type
  })

  const iconMap = {
    warning: 'warning',
    success: 'check_circle',
    error: 'cancel',
    info: 'info'
  }[type]
  const icon = customIcon || iconMap

  return (
    <div className='bg-dialog-bg fixed inset-0 z-50 flex items-center justify-center'>
      <div
        role='button'
        tabIndex={-1}
        aria-label='Close overlay'
        className='absolute inset-0 h-full w-full cursor-default'
        onClick={onClose}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onClose()
          }
        }}
      />

      <div
        ref={dialogRef}
        role='alertdialog'
        aria-modal='true'
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className='shadow-dialog bg-surface-white-50 4xl:px-8 4xl:pb-10 4xl:pt-8 4xl:max-w-lg relative z-30 w-full max-w-md rounded-2xl bg-white px-8 pb-8 pt-6'
      >
        {!hideCloseButton && (
          <div className='flex items-center justify-end'>
            <button
              onClick={onClose}
              className='text-surface-black-50 cursor-pointer'
              aria-label='Close'
            >
              <span className='material-symbols-rounded text-title-lg text-surface-black-50 [--_fill:1]'>
                close
              </span>
            </button>
          </div>
        )}
        <div className='flex flex-col items-center justify-center'>
          <div
            className={cn(
              iconWrapperSlot(),
              iconBackground,
              iconSizeConfig[iconSize].wrapper
            )}
          >
            {icon && (
              <span
                className={cn(
                  iconSlot(),
                  iconSizeConfig[iconSize].text,
                  iconFill
                )}
              >
                {icon}
              </span>
            )}
          </div>

          <div id={titleId} className='mb-2 text-center'>
            <Text
              size={titleSize}
              weight='semibold'
              color='text-surface-black-50'
            >
              {title}
            </Text>
          </div>

          {description && (
            <div id={descriptionId} className='4xl:mb-6 mb-4 text-center'>
              <Text size='caption-md' color='text-surface-white-400'>
                {description}
              </Text>
            </div>
          )}

          <div
            className={cn(
              'flex w-full items-center justify-center gap-2',
              orientation === 'row' ? 'flex-row' : 'flex-col'
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}
